import { Skeleton } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { useState } from "react";

import {
  getListJobsQueryKey,
  useGetCatalog,
} from "../../../api/generated/endpoints/generation/generation";
import type { AiActorGender } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { ActorsComposer } from "../../../platform/actors/composer/ActorsComposer";
import { actorModels } from "../../../platform/actors/composer/GenerateWithText";
import {
  GlobalActors,
  NO_ACTORS_QUERY,
  type ActorsQuery,
} from "../../../platform/actors/library/GlobalActors";
import { MyActors } from "../../../platform/actors/library/MyActors";
import { useCreateTalkingHead } from "../../../platform/actors/talking-head-handoff";
import create from "../create/create.module.css";
import { RecentResults } from "../create/RecentResults";
import classes from "./actors.module.css";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/home2/actors";
const ACTORS = "/teams/$teamId/brands/$brandId/home2/actors";
/** The AI Actors page's My Generations read, so the two share it. */
const ACTOR_TYPES = ["image.actor"] as const;

type Tab = "global" | "team" | "make";

/** The prototype's three tabs, in its order; Global actors is its first. */
const TABS: { tab: Tab; label: string }[] = [
  { tab: "global", label: "Global actors" },
  { tab: "team", label: "Your team's actors" },
  { tab: "make", label: "Make an actor" },
];

/** On a phone (the shell's own breakpoint), two columns of actors, whatever the saved setting. */
const PHONE_COLUMNS = 2;

/** A tab's link: the first has no `?tab`. */
const searchOf = (tab: Tab) => ({ tab: tab === "global" ? undefined : tab });

/**
 * The prototype's `#actors` and `#create-actor`: Global actors and Your team's actors are the AI
 * Actors page's own lists (its search, filters, cards, rename and delete), and Make an actor its
 * composer in the Create card beside the recent actor results (spec ui-perf/pro-studio-redesign,
 * PR c5). An actor's "Talking head" opens the redesign's Talking head page with it.
 */
export function ActorsPage() {
  const { teamId, brandId } = useParams({ from: ROUTE });
  // The brand menu keeps the page mounted: another brand starts the lists' search and facets
  // anew, so My Actors never asks for the last brand's product.
  return <ActorsLists key={`${teamId} ${brandId}`} teamId={teamId} brandId={brandId} />;
}

/** One brand's Actors page: its tabs, and the lists' search and facets kept across them. */
function ActorsLists({ teamId, brandId }: { teamId: string; brandId: string }) {
  const tab: Tab = useSearch({ from: ROUTE }).tab ?? "global";
  const talkingHead = useCreateTalkingHead("redesign");
  const phone = useMediaQuery("(max-width: 48em)") === true;
  const columnsAtMost = phone ? PHONE_COLUMNS : undefined;
  // The lists' search, order and facets are the page's, kept across the tabs, as on AI Actors.
  const [query, setQuery] = useState<ActorsQuery>(NO_ACTORS_QUERY);
  const [gender, setGender] = useState<AiActorGender | undefined>(undefined);
  const [creators, setCreators] = useState<string[]>([]);
  const [product, setProduct] = useState<string | undefined>(undefined);
  const onQuery = (change: Partial<ActorsQuery>) => setQuery((current) => ({ ...current, ...change }));
  return (
    <div className={create.page}>
      <div className={classes.head}>
        <div className={classes.titles}>
          <h1 className={classes.title}>Actors</h1>
          <p className={classes.lede}>The people in your ads. Pick a global actor or make your own.</p>
        </div>
        <Link
          to={ACTORS}
          params={{ teamId, brandId }}
          search={searchOf("make")}
          className={classes.primary}
        >
          Make an actor
        </Link>
      </div>
      <nav className={classes.tabs} aria-label="Actors tabs">
        {TABS.map((each) => (
          <Link
            key={each.tab}
            to={ACTORS}
            params={{ teamId, brandId }}
            search={searchOf(each.tab)}
            className={classes.tab}
            aria-current={each.tab === tab ? "page" : undefined}
            activeOptions={{ exact: true, includeSearch: true }}
          >
            {each.label}
          </Link>
        ))}
      </nav>
      {tab === "make" ? (
        <MakeActor teamId={teamId} brandId={brandId} />
      ) : tab === "team" ? (
        <MyActors
          teamId={teamId}
          brandId={brandId}
          query={query}
          onQuery={onQuery}
          gender={gender}
          onGender={setGender}
          creators={creators}
          onCreators={setCreators}
          product={product}
          onProduct={setProduct}
          onTalkingHead={talkingHead}
          columnsAtMost={columnsAtMost}
        />
      ) : (
        <GlobalActors
          teamId={teamId}
          brandId={brandId}
          query={query}
          onQuery={onQuery}
          onTalkingHead={talkingHead}
          columnsAtMost={columnsAtMost}
        />
      )}
    </div>
  );
}

/** Make an actor: the AI Actors page's composer in the Create card, then the recent actors. */
function MakeActor({ teamId, brandId }: { teamId: string; brandId: string }) {
  const queryClient = useQueryClient();
  const catalog = useGetCatalog();
  const composer = catalog.isError ? (
    <ErrorAlert error={catalog.error} />
  ) : catalog.data === undefined ? (
    <Skeleton className={create.composerSkeleton} />
  ) : actorModels(catalog.data.image_models).length > 0 ? (
    // A brand's own draft, as the Studio's composer is keyed (decision 20).
    <ActorsComposer
      key={brandId}
      teamId={teamId}
      brandId={brandId}
      catalog={catalog.data}
      // The AI Actors page's own: its lists are read again once an actor starts.
      onStarted={() =>
        void queryClient.invalidateQueries({ queryKey: getListJobsQueryKey(teamId) })
      }
      inline
    />
  ) : (
    <p className={create.dim}>No model makes actors yet.</p>
  );
  return (
    <div className={create.create}>
      <div className={create.composer}>{composer}</div>
      <section aria-label="Results">
        <RecentResults teamId={teamId} brandId={brandId} types={ACTOR_TYPES} noun="actor" />
      </section>
    </div>
  );
}
